import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { Alert, Checkbox, FormActions, FormSection, Input, PageHeader, Spinner, Textarea } from '../components/ui'

const empty = { title: '', message: '', expires_at: '', is_prioritary: false, is_active: 1 }

export default function NotificationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const editing = Boolean(id)
  const [form, setForm] = useState(empty)
  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!editing) return
    let active = true
    adminApi.listNotifications()
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : data.data || []
        const item = items.find((entry) => String(entry.id) === String(id))
        if (!item) throw new Error('Notificação não encontrada.')
        if (active) {
          setForm({
            title: item.title || '',
            message: item.message || '',
            expires_at: item.expires_at?.slice(0, 16) || '',
            is_prioritary: Boolean(item.is_prioritary),
            is_active: item.is_active,
          })
        }
      })
      .catch((err) => active && setError(getApiErrorMessage(err, 'Não foi possível carregar a notificação.')))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [editing, id])

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }))

  async function submit(event) {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    setError('')
    const body = {
      title: form.title,
      message: form.message,
      expires_at: form.expires_at || null,
      is_prioritary: form.is_prioritary,
    }
    try {
      if (editing) await adminApi.updateNotification(id, { ...body, is_active: form.is_active ?? 1 })
      else await adminApi.createNotification(body)
      navigate('/notifications', { replace: true, state: { message: editing ? 'Notificação atualizada.' : 'Notificação criada.' } })
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spinner label="Carregando notificação…" />

  return (
    <form onSubmit={submit} className="space-y-6">
      <PageHeader eyebrow="Comunicação" title={editing ? 'Editar notificação' : 'Nova notificação'} description="Defina a mensagem exibida aos clientes e seu período de validade." icon="fa-bell" />
      {error && <Alert type="error">{error}</Alert>}
      <FormSection title="Conteúdo" description="Use uma mensagem curta, objetiva e adequada a todos os clientes." icon="fa-message">
        <div className="space-y-4">
          <Input label="Título *" value={form.title} onChange={(e) => update('title', e.target.value)} required autoFocus />
          <Textarea label="Mensagem *" rows={7} value={form.message} onChange={(e) => update('message', e.target.value)} required />
        </div>
      </FormSection>
      <FormSection title="Publicação" description="Controle prioridade, validade e visibilidade." icon="fa-calendar-check">
        <div className="grid gap-4 md:grid-cols-2">
          <Input label="Expira em" type="datetime-local" value={form.expires_at} onChange={(e) => update('expires_at', e.target.value)} hint="Deixe em branco para não definir expiração." />
          <Checkbox label="Notificação prioritária" description="Exibir também na tela de login dos clientes." checked={form.is_prioritary} onChange={(e) => update('is_prioritary', e.target.checked)} />
          {editing && (
            <Checkbox label="Notificação ativa" description="Desmarque para interromper a exibição sem excluir o registro." checked={Boolean(form.is_active)} onChange={(e) => update('is_active', e.target.checked ? 1 : 0)} />
          )}
        </div>
      </FormSection>
      <FormActions onCancel={() => navigate('/notifications')} saving={saving} submitLabel={editing ? 'Salvar alterações' : 'Publicar notificação'} />
    </form>
  )
}
